import React, { useRef, useEffect, useLayoutEffect, ReactNode, useState } from 'react'
import styled, { ThemeProvider } from 'styled-components'
import ReactGA from 'react-ga'
import { Location } from '@reach/router'
import { throttle } from 'throttle-debounce'

import Sidebar from '../components/sidebar'
import Header from './header'
import { containerWidth, contentWidth, headerMargin, gaCode } from './constants'
import { desktop, mobile } from './utils/media-query'
import { NavigationContext } from './context-link'

const DOCS_VIEWPORT_CONTENT = 'width=1240, user-scalable=yes, shrink-to-fit=no'

const LayoutContainer = styled.div`
    display: flex;
    justify-content: center;
    flex-direction: column;
`

const HeaderContainer = styled.div<{ hasMargin?: boolean }>`
    margin: 0 auto;
    position: sticky;
    top: 0;
    background: black;
    z-index: 2;
    margin-bottom: ${props => (props.hasMargin === false ? '0' : `${headerMargin}px`)};
    width: 100%;
    ${desktop.css`
        min-width: ${containerWidth}px;
    `}

    ${mobile.css`
        margin-bottom: 10px;
    `}
`

// center the docs, fixed width
// TODO: sidebar is fixed, which breaks horizontal scroll
const DocsContainerComponent = styled.div`
    display: flex;
    width: ${containerWidth}px;
    margin: 0 auto;
`

const DocsContainer: React.FC = ({ children }) => {
    // react-helmet does not clean up so hooks are used instead
    useLayoutEffect(() => {
        const viewportElement = document.querySelector('meta[name="viewport"]')
        if (viewportElement) {
            const previousContent = viewportElement.getAttribute('content')
            viewportElement.setAttribute('content', DOCS_VIEWPORT_CONTENT)
            return () => {
                if (previousContent) viewportElement.setAttribute('content', previousContent)
            }
        }
    }, [])
    return <DocsContainerComponent>{children}</DocsContainerComponent>
}

const ContentContainer = styled.div`
    width: ${contentWidth}px;
`

// don't let content be smaller than header,
// for when screen is smaller than header
// hide page overflow for marquee
const LandingPageContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;

    ${desktop.css`
        min-width: ${containerWidth}px;
        overflow: hidden;
    `}
`

// Vertical offset to prevent wrong items in sidebar to be highlighted as active
// because of scroll event firing for anchor navigation
const VERTICAL_OFFSET = 50

const ScrollSpyContainer = ({
    children,
    onNavigationChange,
}: {
    children: ReactNode
    onNavigationChange: (route: string) => void
}) => {
    const containerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        onNavigationChange(location.pathname + location.hash)
        const el = document.getElementById(location.hash.substr(1))
        if (el) el.scrollIntoView()

        if (!containerRef || !containerRef.current) {
            return
        }

        const elements = containerRef.current.querySelectorAll<HTMLElement>('[data-type="anchor"]')
        const anchors = Array.from(elements).map(item => ({
            position: item.offsetTop,
            hash: `#${item.id}`,
            pathname: item.getAttribute('data-pathname'),
        }))

        // TODO: Consider adding scroll debounce for higher performance / responsiveness
        const handleScroll = throttle(400, () => {
            if (!anchors.length) {
                return
            }

            const yPosition = window.pageYOffset + VERTICAL_OFFSET

            // Reset hash when viewport is above first anchor
            if (yPosition < anchors[0].position) {
                history.pushState(null, '', location.pathname)
                onNavigationChange(location.pathname)
                return
            }

            let activeAnchor = null

            // Loop through page anchors and find lowest anchor above current viewport position
            for (let i = 1, prev = anchors[i - 1], cur = anchors[i]; (cur = anchors[i]); i++) {
                prev = anchors[i - 1]

                if (yPosition >= prev.position && yPosition < cur.position) {
                    activeAnchor = prev
                    break
                }

                activeAnchor = cur
            }

            if (activeAnchor && location.hash !== activeAnchor.hash) {
                const path = activeAnchor.pathname
                    ? activeAnchor.pathname + activeAnchor.hash
                    : location.pathname + activeAnchor.hash
                history.pushState(null, '', path)
                onNavigationChange(path)
            }
        })

        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    return <ContentContainer ref={containerRef}>{children}</ContentContainer>
}

const Layout = ({
    children,
    withSidebar = false,
    hasLogin = false,
}: {
    children: ReactNode
    withSidebar?: boolean
    hasLogin?: boolean
}) => {
    const [route, setRoute] = useState('')

    useEffect(() => {
        ReactGA.initialize(gaCode)
    }, [])

    return (
        <LayoutContainer>
            <NavigationContext.Provider value={{ onNavigationChange: setRoute }}>
                <Location>
                    {({ location }) => (
                        <ThemeProvider theme={{ disableMobileQueries: location.pathname.includes('docs') }}>
                            <Location>
                                {location => (
                                    <HeaderContainer hasMargin={!location.location.pathname.includes('faq')}>
                                        <Header hasLogin={hasLogin} />
                                    </HeaderContainer>
                                )}
                            </Location>
                            {withSidebar ? (
                                <DocsContainer>
                                    <Sidebar route={route} />
                                    <ScrollSpyContainer onNavigationChange={setRoute}>{children}</ScrollSpyContainer>
                                </DocsContainer>
                            ) : (
                                <LandingPageContainer>{children}</LandingPageContainer>
                            )}
                        </ThemeProvider>
                    )}
                </Location>
            </NavigationContext.Provider>
        </LayoutContainer>
    )
}

export default Layout
